import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import {
  useGetUserOptionQuery,
  useUpdateUserOptionMutation,
} from '@/features/user-options/gql/_gen_/user-options.gql';
import { parseContent, parseHTML } from '@/lib/tiptap';

interface UseNotepadProps {
  brandCode?: string;
  userId?: string | null;
}

const useNotepad = ({ brandCode, userId }: UseNotepadProps) => {
  const { toast } = useToast();
  const [notepad, setNotepad] = useState<string | null>(null);
  const [savedValue, setSavedValue] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);

  const skip =
    !brandCode ||
    brandCode === undefined ||
    brandCode === null ||
    brandCode === 'undefined' ||
    !userId ||
    userId === undefined ||
    userId === null ||
    userId === 'undefined';

  const { data, loading } = useGetUserOptionQuery({
    skip,
    variables: {
      brandCode: String(brandCode),
      userId: String(userId),
    },
  });

  const [updateUserOption, { loading: loadingSave }] = useUpdateUserOptionMutation({
    onError: (error) => {
      toast({
        title: 'Notepad Update Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onCompleted: (data) => {
      if (data?.updateUserOption?.appNotes) {
        const parsedContent = parseContent(data?.updateUserOption?.appNotes);
        const htmlContent = parseHTML(parsedContent);
        setNotepad(htmlContent);
        setSavedValue(htmlContent);
      }
      toast({
        title: 'Updated',
        description: `Your notepad for ${brandCode} has been updated.`,
      });
    },
  });

  useEffect(() => {
    if (data) {
      if (data.getUserOption?.appNotes) {
        const parsedContent = parseContent(data.getUserOption?.appNotes);
        const htmlContent = parseHTML(parsedContent);
        setNotepad(htmlContent);
        setSavedValue(htmlContent);
      } else {
        setNotepad(null);
        setSavedValue(null);
      }
    }
  }, [data, loading]);

  const saveNotes = useCallback(async () => {
    if (brandCode && userId && editor) {
      const content = editor.getJSON();
      await updateUserOption({
        variables: {
          brandCode,
          userId,
          appNotes: JSON.stringify(content),
        },
        onError: (error) => {
          toast({
            title: 'Notepad Save Error',
            description: error.message,
            variant: 'destructive',
          });
        },
        onCompleted: () => {
          toast({
            title: 'Saved',
            description: `Your notepad for ${brandCode} has been saved.`,
          });
        },
      });
    }
  }, [brandCode, userId, editor, updateUserOption]);

  const setEditorInstance = useCallback((newEditor: Editor | null) => {
    setEditor(newEditor);
  }, []);

  return {
    notepad,
    loadingSave,
    setNotepad,
    saveNotes,
    savedValue,
    setEditorInstance,
  };
};

export { useNotepad };
